import isEmpty from 'lodash/isEmpty';
import { cloneElement, isValidElement } from 'react';
import { Exit16 } from 'ui/icons/aurora/16px/Exit16';
import { BUTTON_SIZES, IconButton } from 'ui/button';
import {
  BannerActionsWrapper,
  BannerBase,
  BannerBodyBase,
  BannerCloseBtnWrapper,
  BannerIconWrapper,
  BannerContent,
  BannerTitleBase,
} from './Banner.styles';
import { BannerProps, BannerVariants } from './types';
import { getCloseBtnVariant } from './utils';

export const Banner = ({
  children,
  BannerActions,
  Icon,
  isInlineTitle = false,
  isSubtle,
  onClose,
  title = '',
  variant = BannerVariants.SUCCESS,
  ...props
}: BannerProps) => {
  const closeBtnVariant = getCloseBtnVariant(variant);
  const IconCmp =
    Icon !== undefined && isValidElement(Icon)
      ? cloneElement(Icon, { isColorInherited: true, ...Icon.props })
      : undefined;

  /**
   * If there is only a close button and body text on a banner, the close button height misaligns
   * the text. So we need to set it to center align.
   *
   * See: https://iterable.atlassian.net/browse/FE-1306 for an example
   */
  const bannerOnlyHasCloseButtonAndText = !BannerActions && onClose && !title;

  return (
    <BannerBase
      data-test={`banner-${variant}`}
      role="alert"
      variant={variant}
      data-subtle={!!isSubtle}
      data-variant={variant}
      data-close-button-only-variant={bannerOnlyHasCloseButtonAndText}
      {...props}
    >
      {IconCmp && <BannerIconWrapper>{IconCmp}</BannerIconWrapper>}
      <BannerContent data-test="banner-content" className={isInlineTitle ? 'inline-title' : ''}>
        {!isEmpty(title) && <BannerTitleBase data-test="banner-title">{title}</BannerTitleBase>}
        {!isEmpty(children) && (
          <BannerBodyBase data-test="banner-body" data-subtle={!!isSubtle}>
            {children}
          </BannerBodyBase>
        )}
      </BannerContent>
      {BannerActions && (
        <BannerActionsWrapper data-test="banner-actions">{BannerActions}</BannerActionsWrapper>
      )}
      {onClose && (
        <BannerCloseBtnWrapper>
          <IconButton
            data-test={`banner-close-${closeBtnVariant}`}
            onClick={onClose}
            variant={closeBtnVariant}
            size={BUTTON_SIZES.SMALL}
            aria-label="Close"
          >
            <Exit16 />
          </IconButton>
        </BannerCloseBtnWrapper>
      )}
    </BannerBase>
  );
};
